import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Sparkles, Droplet, Sun, FlaskConical } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const SkinHealth = () => {
  const lang = useLanguage();
  const t = getPageTexts('skinHealth', lang);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const treatments = [
    { icon: Droplet, name: t.t1Name, sub: t.t1Sub, desc: t.t1Desc },
    { icon: Sun, name: t.t2Name, sub: t.t2Sub, desc: t.t2Desc },
    { icon: Sparkles, name: t.t3Name, sub: t.t3Sub, desc: t.t3Desc },
    { icon: FlaskConical, name: t.t4Name, sub: t.t4Sub, desc: t.t4Desc },
  ];

  const complementary = [
    { title: t.comp1Title, items: t.comp1Items || [] },
    { title: t.comp2Title, items: t.comp2Items || [] },
    { title: t.comp3Title, items: t.comp3Items || [] },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF8]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.badge}</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1C1C1A] mb-6 leading-tight">
                {t.title1}<br /><span className="italic text-[#B8975A]">{t.title2}</span>
              </h1>
              <div className="w-20 h-[2px] bg-[#B8975A] mb-8"></div>
              <p className="text-base md:text-lg text-[#4A4845] leading-relaxed mb-4">{t.heroDesc1}</p>
              <p className="text-sm text-[#4A4845] leading-relaxed mb-8">{t.heroDesc2}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-[#B8975A] text-white px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
                  {t.requestAppt} <ArrowRight size={16} />
                </Link>
                <a href="tel:+34952764547" className="inline-flex items-center justify-center gap-2 border border-[#1C1C1A] text-[#1C1C1A] px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#1C1C1A] hover:text-white transition-colors">
                  <Phone size={16} /> {t.callNow}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-[#B8975A]"></div>
              <img src="/images/skinhealth-closeup.jpg" alt="Skin Health" className="w-full h-auto" />
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-[#B8975A]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.treatLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">{t.treatTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {treatments.map((tr, i) => (
              <div key={i} className="p-8 border border-[#B8975A]/30 hover:border-[#B8975A] transition-colors">
                <div className="flex items-center gap-4 mb-4">
                  <tr.icon size={32} className="text-[#B8975A] flex-shrink-0" />
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-white">{tr.name}</h3>
                    <p className="text-[#B8975A] text-sm">{tr.sub}</p>
                  </div>
                </div>
                <p className="text-white/70 text-sm leading-relaxed">{tr.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Complementary services */}
      <section className="py-20 bg-[#F0EDE7]">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.compLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.compTitle}</h2>
            <p className="text-sm text-[#4A4845] mt-4">{t.compSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {complementary.map((cat, i) => (
              <div key={i} className="bg-white p-6 border-b-2 border-[#B8975A]">
                <h3 className="font-serif text-lg text-[#1C1C1A] mb-4">{cat.title}</h3>
                <ul className="space-y-2 text-sm text-[#4A4845]">
                  {cat.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 bg-[#FDFBF8]">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-[#B8975A] mb-6">{t.whyLabel}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1C1C1A]">{t.whyTitle}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(t.whyItems || []).map((item, i) => (
              <div key={i} className="bg-[#F0EDE7] p-5 flex items-center gap-3">
                <span className="text-[#B8975A] font-serif text-lg font-semibold">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-sm text-[#1C1C1A]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1C1C1A]">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t.ctaTitle}</h2>
          <p className="text-white/70 mb-10">{t.ctaDesc}</p>
          <Link to="/contacto" className="inline-flex items-center gap-2 bg-[#B8975A] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#96784a] transition-colors">
            {t.ctaBtn} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default SkinHealth;
